import type {
  ChangesPage,
  ChatFileView,
  Deleted,
  HistoryPage,
  MessageView,
  ShownMessage,
  ThreadPage,
} from "@beacon/core/chat"
import { saidBy, sameMember } from "./authors.ts"

/*
 * A conversation as the web app holds it while it is open: the messages read so far, in their
 * order, where an earlier page would start (`before`), and the cursor its changes are caught up to.
 * The record is the server's: everything here is read from it and merged by message id, version
 * and change number, so a page, change or answer that arrives twice, late or out of turn changes
 * nothing. A copy of a message replaces the one held only when it is newer, and a deleted message's
 * id is kept (its tombstone, no more) so that nothing read before its deletion brings it back. A
 * deleted message whose thread still holds a reply is held as its placeholder instead
 * (`deleted: true`, nothing it said), until the thread's last reply goes.
 */

export type Timeline = {
  /** In `seq` order, each once, none deleted but as a thread's placeholder. */
  messages: ShownMessage[]
  /** The `before` to read the page before the first message held, none at the start. */
  before: number | null
  /** The change number everything up to which is held. */
  cursor: number
  /** The ids of the messages known to be deleted while it was open. */
  deleted: readonly string[]
}

/**
 * One message from the copy held and an arriving one, field by field from whichever is newer for
 * it: what it says from the newer by `version`, who reacted from the newer by `reactionsVersion`,
 * and its thread's summary from the newer by the thread's `latestSeq`, since a reaction or a reply
 * moves only its own number and a copy read meanwhile may be ahead on any. A copy at the version
 * held, read before its link previews were found, keeps the previews held. A placeholder, newer
 * than every copy of the message it stands for, takes nothing from them but its thread.
 */
export function newerCopy(holding: ShownMessage, message: ShownMessage): ShownMessage {
  const said = holding.version > message.version ? holding : message
  const threaded =
    (holding.thread?.latestSeq ?? 0) > (message.thread?.latestSeq ?? 0) ? holding : message
  if (said.deleted) return { ...said, thread: threaded.thread ?? said.thread }
  if (holding.deleted || message.deleted) return { ...said, thread: threaded.thread }
  const reactedTo = holding.reactionsVersion > message.reactionsVersion ? holding : message
  // Within one version a message's link previews are only ever found, never lost: a copy read
  // before they were keeps none, and mustn't take away those held.
  const keepPreviews =
    holding.version === message.version &&
    message.linkPreviews.length === 0 &&
    holding.linkPreviews.length > 0
  return {
    ...said,
    linkPreviews: keepPreviews ? holding.linkPreviews : said.linkPreviews,
    reactions: reactedTo.reactions,
    reactionsVersion: reactedTo.reactionsVersion,
    thread: threaded.thread,
  }
}

/**
 * Messages merged by id, in `seq` order, each held and arriving copy as `newerCopy` makes one.
 * None that is deleted is taken in.
 */
function merged(
  held: readonly ShownMessage[],
  arriving: readonly ShownMessage[],
  deleted: readonly string[],
) {
  const gone = new Set(deleted)
  const byId = new Map(held.map((message) => [message.id, message]))
  for (const message of arriving) {
    if (gone.has(message.id)) continue
    const holding = byId.get(message.id)
    byId.set(message.id, holding ? newerCopy(holding, message) : message)
  }
  return [...byId.values()].filter(({ id }) => !gone.has(id)).sort((a, b) => a.seq - b.seq)
}

/** A timeline from the latest page of history. */
export const timelineOf = (page: HistoryPage): Timeline => ({
  messages: page.messages,
  before: page.before,
  cursor: page.latestSeq,
  deleted: [],
})

/** The timeline with an earlier page taken in; its cursor stays, as the page is older. */
export const withOlder = (timeline: Timeline, page: HistoryPage): Timeline => ({
  ...timeline,
  messages: merged(timeline.messages, page.messages, timeline.deleted),
  before: page.before,
})

/**
 * The timeline with the changes after its cursor taken in, its cursor moved on, never back. A
 * timeline holds the messages of one place: the conversation itself (`thread` none), where replies
 * are not said, or the thread of the message `thread`, which holds its replies alone. Each message
 * it holds that a thread started from takes its thread's summary from the page's `roots`. A
 * deletion that leaves a placeholder puts it in the message's place; a placeholder whose thread
 * the page changed, and whose root the page no longer names, went with its last reply.
 */
export function withChanges(
  timeline: Timeline,
  page: ChangesPage,
  thread: string | null = null,
): Timeline {
  const here = (threadRootId: string | null) => threadRootId === thread
  const named = new Set(page.roots.map(({ id }) => id))
  const touched = new Set(page.changes.flatMap((change) => threadOfChange(change) ?? []))
  const deleted = [
    ...timeline.deleted,
    ...page.changes.flatMap((change) =>
      change.kind === "message-deleted" && here(change.threadRootId) && !change.placeholder
        ? [change.messageId]
        : [],
    ),
    ...timeline.messages.flatMap(({ id, deleted }) =>
      deleted && touched.has(id) && !named.has(id) ? [id] : [],
    ),
  ]
  const arriving = page.changes.flatMap((change): ShownMessage[] => {
    if ("message" in change) return here(change.message.threadRootId) ? [change.message] : []
    if (change.kind === "message-deleted" && here(change.threadRootId) && change.placeholder)
      return [change.placeholder]
    return []
  })
  const held = new Set(timeline.messages.map(({ id }) => id))
  const roots = page.roots.filter(({ id }) => held.has(id))
  return {
    ...timeline,
    messages: merged(merged(timeline.messages, arriving, deleted), roots, deleted),
    cursor: Math.max(timeline.cursor, page.cursor),
    deleted,
  }
}

/**
 * The timeline with a message a thread started from as it is now (a reply's answer brings it), if
 * the timeline holds it, taken in as `newerCopy` takes a copy: a held message's thread summary
 * moves on, and nothing else arrives by it.
 */
export function withRoot(timeline: Timeline, root: ShownMessage): Timeline {
  if (!timeline.messages.some(({ id }) => id === root.id)) return timeline
  return { ...timeline, messages: merged(timeline.messages, [root], timeline.deleted) }
}

/**
 * The timeline with a message the Member just sent placed in it. The cursor stays: the changes
 * since it, the message's own among them, are still to be read.
 */
export const withSent = (timeline: Timeline, message: MessageView): Timeline => ({
  ...timeline,
  messages: merged(timeline.messages, [message], timeline.deleted),
})

/**
 * A thread as the web app holds it while it is open: its replies as a timeline of their own, under
 * the message the thread started from (`root`), its placeholder once that is deleted, and none
 * once the thread is gone (its message deleted with no reply left); and whether the Member follows
 * it.
 */
export type ThreadTimeline = Timeline & { root: ShownMessage | null; following: boolean }

/** The thread a change is in, by the id of its message: none for a change outside threads. */
function threadOfChange(change: ChangesPage["changes"][number]) {
  if ("message" in change) return change.message.threadRootId ?? undefined
  if (change.kind === "message-deleted") return change.threadRootId ?? undefined
  return undefined
}

/** A thread's timeline from its latest page. */
export const threadTimelineOf = (page: ThreadPage): ThreadTimeline => ({
  ...timelineOf({ messages: page.replies, before: page.before, latestSeq: page.latestSeq }),
  root: page.root,
  following: page.following,
})

/** The page of a thread's replies as a page of history, for `withOlder`. */
export const repliesPage = (page: ThreadPage): HistoryPage => ({
  messages: page.replies,
  before: page.before,
  latestSeq: page.latestSeq,
})

/**
 * The thread with the changes after its cursor taken in: its replies, as `withChanges` takes them,
 * and its message, edited, or deleted into its placeholder. The thread is gone when its message
 * is deleted with no reply left, at once or as its last reply goes (the page changed the thread
 * and no longer names its root); a gone thread takes nothing more.
 */
export function withThreadChanges(thread: ThreadTimeline, page: ChangesPage): ThreadTimeline {
  const root = thread.root
  if (!root) return { ...thread, cursor: Math.max(thread.cursor, page.cursor) }
  const deletion = page.changes.find(
    (change) => change.kind === "message-deleted" && change.messageId === root.id,
  )
  const placeholder = deletion?.kind === "message-deleted" ? deletion.placeholder : undefined
  const shown = page.roots.find(({ id }) => id === root.id)
  const emptied =
    (root.deleted || placeholder !== undefined) &&
    !shown &&
    page.changes.some((change) => threadOfChange(change) === root.id)
  if ((deletion && !placeholder) || emptied)
    return {
      ...thread,
      root: null,
      messages: [],
      deleted: [...thread.deleted, ...thread.messages.map(({ id }) => id)],
      cursor: Math.max(thread.cursor, page.cursor),
    }
  const own = page.changes.flatMap((change): ShownMessage[] =>
    "message" in change && change.message.id === root.id ? [change.message] : [],
  )
  const replies = withChanges(thread, page, root.id)
  const now = [...own, ...(placeholder ? [placeholder] : []), ...(shown ? [shown] : [])].reduce(
    newerCopy,
    root,
  )
  return { ...replies, root: now, following: thread.following }
}

/** The thread with a copy of its message taken in as `newerCopy` takes one (a reply's answer). */
export function withThreadRoot(thread: ThreadTimeline, root: ShownMessage): ThreadTimeline {
  if (!thread.root || thread.root.id !== root.id) return thread
  return { ...thread, root: newerCopy(thread.root, root) }
}

/** The timeline with the message as the Member's edit left it; the cursor stays, as on sending. */
export const withEdited = withSent

/** The timeline with the message as the Member's reaction left it; the cursor stays, as on sending. */
export const withReacted = withSent

/**
 * The timeline without a message the Member just deleted, by the tombstone deleting answered, or
 * with its placeholder in its place when its thread still holds a reply.
 */
export function withDeleted(timeline: Timeline, tombstone: Deleted): Timeline {
  if (tombstone.placeholder)
    return {
      ...timeline,
      messages: merged(timeline.messages, [tombstone.placeholder], timeline.deleted),
    }
  const deleted = [...timeline.deleted, tombstone.messageId]
  return { ...timeline, messages: merged(timeline.messages, [], deleted), deleted }
}

/**
 * Whether the pages caught up hold a change to the conversation itself, rather than to a message:
 * someone joining or leaving, becoming a manager or stopping, the channel archived or unarchived.
 * Its view and members are read again on one. Its pins or bookmarks changing are not: they are read
 * again alone (`collectionsTouched`).
 */
export const changesTheConversation = (pages: readonly ChangesPage[]) =>
  pages.some(({ changes }) =>
    changes.some(
      ({ kind }) =>
        !kind.startsWith("message-") && kind !== "pins-changed" && kind !== "bookmarks-changed",
    ),
  )

/** The messages `after` holds that `before` didn't and someone other than `memberId` sent. */
export function arrivedFromOthers(
  before: Timeline,
  after: Timeline,
  memberId: string,
): MessageView[] {
  const held = new Set(before.messages.map(({ id }) => id))
  return after.messages.flatMap((message) =>
    message.deleted || held.has(message.id) || saidBy(message, memberId) ? [] : [message],
  )
}

/**
 * A message the Member sent and the server hasn't answered yet (`sending`), or that didn't reach it
 * (`failed`), by the operation id every try of it carries, with the files it carries.
 */
export type Unsent = {
  operationId: string
  body: string
  files: readonly ChatFileView[]
  state: "sending" | "failed"
  /** Whether it asks Beacon, so sending it again asks again. */
  asks?: boolean
}

/** The unsent messages with `operationId` sending (again, or for the first time, last). */
export function sending(
  list: readonly Unsent[],
  operationId: string,
  body: string,
  files: readonly ChatFileView[] = [],
  asks = false,
): Unsent[] {
  const shown: Unsent = { operationId, body, files, state: "sending", ...(asks && { asks }) }
  return list.some((each) => each.operationId === operationId)
    ? list.map((each) => (each.operationId === operationId ? shown : each))
    : [...list, shown]
}

/** The unsent messages with `operationId` failed. */
export const failed = (list: readonly Unsent[], operationId: string): Unsent[] =>
  list.map((each) => (each.operationId === operationId ? { ...each, state: "failed" } : each))

/** The unsent messages without `operationId`, once it is sent or given up. */
export const unsent = (list: readonly Unsent[], operationId: string): Unsent[] =>
  list.filter((each) => each.operationId !== operationId)

/**
 * The unsent messages with those kept on this device from before (a reload) after them, each
 * failed: whether it arrived is unknown until the Member sends it again, as the same operation.
 */
export const withKept = (
  list: readonly Unsent[],
  kept: readonly Omit<Unsent, "state">[],
): Unsent[] => [
  ...list,
  ...kept
    .filter(({ operationId }) => !list.some((each) => each.operationId === operationId))
    .map((each): Unsent => ({ ...each, state: "failed" })),
]

/** How long after one message the next by the same author still belongs to its run. */
const runGap = 5 * 60_000

/**
 * The messages in runs: one author's messages, each sent within five minutes of the one before,
 * shown under one name and photo. A placeholder stands in a run of its own.
 */
export function runsOf(messages: readonly ShownMessage[]): ShownMessage[][] {
  const runs: ShownMessage[][] = []
  for (const message of messages) {
    const run = runs.at(-1)
    const last = run?.at(-1)
    if (
      run &&
      last &&
      // One Member's messages run together; each of Beacon's replies is a run of its own.
      sameMember(last.author, message.author) &&
      Date.parse(message.sentAt) - Date.parse(last.sentAt) <= runGap
    )
      run.push(message)
    else runs.push([message])
  }
  return runs
}
